import { Form, Formik, FormikHelpers } from 'formik';
import { useRouter } from 'next/router';
import { useLocationDetails } from 'hooks/api/useLocationDetails';
import { LoadingIndicator } from 'components/core/LoadingIndicator/LoadingIndicator';
import { SignupAccountDetailsForm } from 'components/views/pages/signup/components/SignupAccountDetailsForm';
import {
  addressValidation,
  phone,
  TwilioVerificationChannelEnum,
  useToggle,
  useUserPutMutation,
  useTwilioSendCodeMutation,
} from '@alluradirect/react';
import * as yup from 'yup';
import Grid from 'components/core/Grid';
import * as Sentry from '@sentry/nextjs';
import { toast } from 'react-toastify';

const initialValues = {
  ADDRESS: {
    UNIT: '',
    STREET_NUMBER: '',
    STREET_NAME: '',
    CITY: '',
    COUNTRY: '',
    COUNTRY_ID: 0,
    REGION: '',
    REGION_ID: 0,
    POSTAL: '',
  },
  PHONE: '',
  DOB: {
    YEAR: '',
    MONTH: '',
    DAY: '',
  },
};

export type SignupAccountDetailsFormikValues = typeof initialValues;

const validationSchema = yup.object().shape({
  ADDRESS: yup.object().shape(addressValidation),
  PHONE: phone,
  DOB: yup.object().shape({
    YEAR: yup.string().required().label('Year of Birth'),
    MONTH: yup.string().required().label('Month of Birth'),
    DAY: yup.string().required().label('Day of Birth'),
  }),
});

export type SignupAccountDetailsOnSuccess = (data: { id: any; firstName: any; lastName: any; email: any }) => void;

interface Props {
  hasTopAppDivider?: boolean;
  onSuccess: SignupAccountDetailsOnSuccess;
}

export const SignupAccountDetails = ({ hasTopAppDivider = true, onSuccess }: Props) => {
  const togglePhoneVerification = useToggle();
  const router = useRouter();
  const guestId = router.query.userId as string | undefined;
  const guestEmail = router.query.userEmail;
  const guestFirstName = router.query.userFirstName;
  const guestLastName = router.query.userLastName;

  const sendCode = useTwilioSendCodeMutation();
  const updateUser = useUserPutMutation();
  const locationDetails = useLocationDetails({
    isOwnerApp: false,
    guestId: guestId as any,
  });

  const onVerificationSuccess = async (vals: SignupAccountDetailsFormikValues, setSubmitting: any) => {
    const dateOfBirth = `${vals.DOB.YEAR}-${vals.DOB.MONTH}-${vals.DOB.DAY}`;
    const submitValues = {
      ...vals,
      ...vals.ADDRESS,
      IS_COMPANY: false,
      COMPANY_NAME: '',
      ID: guestId as any,
      EMAIL: guestEmail as any,
      FIRST_NAME: guestFirstName as any,
      LAST_NAME: guestLastName as any,
      DATE_OF_BIRTH: dateOfBirth,
    };

    try {
      await updateUser.mutateAsync(submitValues);
      onSuccess({
        email: guestEmail,
        id: guestId,
        firstName: guestFirstName,
        lastName: guestLastName,
      });
    } catch (e) {
      Sentry.captureException(e);
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmit = async (values: SignupAccountDetailsFormikValues, helpers: FormikHelpers<any>) => {
    try {
      await sendCode.mutateAsync({
        channel: TwilioVerificationChannelEnum.sms,
        value: values.PHONE,
      });
      togglePhoneVerification.open();
    } catch (e) {
      Sentry.captureException(e);
      toast.error('Failed to send phone verification code');
    } finally {
      helpers.setSubmitting(false);
    }
  };

  if (!locationDetails.data)
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <LoadingIndicator height={'80vh'} />
        </Grid>
      </Grid>
    );

  return (
    <>
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
        isInitialValid={false}
      >
        <Form>
          <SignupAccountDetailsForm
            locationDetails={locationDetails.data}
            togglePhoneVerification={togglePhoneVerification}
            hasTopAppDivider={hasTopAppDivider}
            onVerificationSuccess={onVerificationSuccess}
            guestId={guestId}
          />
        </Form>
      </Formik>
    </>
  );
};
